import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { fmtNumber } from "@/lib/prices";
import { Plus, Check, Wrench, ShoppingBag, X } from "lucide-react";import { Link } from "react-router-dom";

const fmtPrice = (n) => (n == null ? "—" : `${fmtNumber(n)} €`);

function tailleToSlug(t) {
  return (t || "std").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function SuggestRow({ acc, onAdd, addedSlugs, featured }) {
  const variants = acc.variantes && acc.variantes.length > 0 ? acc.variantes : [{ taille: "Standard", prix_ttc: acc.prix_ttc, prix_ht: acc.prix_ht }];
  const [vIdx, setVIdx] = useState(0);
  const v = variants[Math.min(vIdx, variants.length - 1)];
  const hasMultiple = variants.length > 1;
  const variantSlug = `${acc.slug}__${tailleToSlug(v.taille)}`;
  const isAdded = addedSlugs.has(variantSlug);

  return (
    <div className={`flex items-center gap-3 p-3 border rounded-sm transition-colors ${
      featured ? "border-[#D8B86A] bg-[#D8B86A]/5 hover:bg-[#D8B86A]/10" : "border-gray-200 hover:border-[#D8B86A] bg-white"
    }`}>
      <div className="w-14 h-14 flex-shrink-0 bg-[#F9F9F9] rounded-sm flex items-center justify-center overflow-hidden relative">
        {featured && (
          <span className="absolute -top-1 -left-1 text-[8px] font-mono-tech font-black tracking-wider uppercase bg-[#D8B86A] text-[#072135] px-1 py-0.5 rounded-sm">★</span>
        )}
        {acc.image ? <img src={acc.image} alt={acc.nom} className="max-w-full max-h-full object-contain p-1" /> : <Wrench size={20} className="text-gray-400" />}
      </div>
      <div className="flex-1 min-w-0">
        {featured && <div className="text-[10px] font-mono-tech tracking-wider text-[#D8B86A] uppercase font-bold">Recommandé</div>}
        <div className="font-heading font-bold text-[#072135] text-sm leading-tight">{acc.nom}</div>
        {acc.contenu && acc.contenu.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {acc.contenu.map((c) => (
              <li key={c} className="flex items-start gap-1 text-[11px] text-[#072135] leading-snug">
                <Check size={11} className="text-[#D8B86A] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        )}
        {acc.recommendation && (
          <div className="text-[10px] text-[#4B5563] italic mt-1 leading-snug">{acc.recommendation}</div>
        )}
        <div className="flex flex-wrap gap-1 mt-1">
          {variants.map((vr, i) => (
            <button
              key={vr.taille}
              onClick={() => hasMultiple && setVIdx(i)}
              disabled={!hasMultiple}
              data-testid={`suggest-${acc.slug}-variant-${i}`}
              className={`px-1.5 py-0.5 text-[10px] font-mono-tech rounded-sm border transition-colors ${
                vIdx === i ? "bg-[#072135] text-white border-[#072135]" : "bg-white text-[#072135] border-gray-300 hover:border-[#072135]"
              } ${!hasMultiple ? "cursor-default" : ""}`}
            >
              {vr.taille}
            </button>
          ))}
        </div>
        <div className="text-xs font-mono-tech text-[#4B5563] mt-1 font-bold">{fmtPrice(v.prix_ttc)} TTC</div>
      </div>
      <button
        onClick={() => onAdd(acc, v, variantSlug)}
        disabled={isAdded}
        data-testid={`suggest-add-${acc.slug}`}
        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-heading font-black uppercase tracking-wider rounded-sm transition-colors ${
          isAdded ? "bg-[#D8B86A]/20 text-[#072135] cursor-default" : featured ? "bg-[#D8B86A] text-[#072135] hover:bg-[#c2a45d]" : "bg-[#072135] text-white hover:bg-[#D8B86A] hover:text-[#072135]"
        }`}
      >
        {isAdded ? <><Check size={13} /> Ajouté</> : <><Plus size={13} /> Ajouter</>}
      </button>
    </div>
  );
}

export default function AccessorySuggestionModal({ open, onClose, machineSlug, machineName, variantName = "" }) {
  const { addItem, setOpen: setCartOpen } = useCart();
  const [accs, setAccs] = useState([]);
  const [addedSlugs, setAddedSlugs] = useState(new Set());
  const [loading, setLoading] = useState(true);

  // Pack entretien only for Kubota-powered RIPPA. R10 Koop 192f has no maintenance pack.
  const supportsMaintenancePacks = !/koop/i.test(variantName);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setAddedSlugs(new Set());
    api.get("/accessoires").then((r) => {
      const compat = (r.data || []).filter((a) => (a.compatibilites || []).includes(machineSlug));
      setAccs(compat);
    }).finally(() => setLoading(false));
  }, [open, machineSlug]);

  const handleAdd = (acc, variant, variantSlug) => {
    const variants = acc.variantes && acc.variantes.length > 0 ? acc.variantes : [{ taille: "Standard" }];
    const hasMultiple = variants.length > 1;
    addItem({
      slug: variantSlug,
      type: "accessoire",
      nom: hasMultiple ? `${acc.nom} — ${variant.taille}` : acc.nom,
      prix_ht: variant.prix_ht,
      prix_ttc: variant.prix_ttc,
      image: acc.image,
    });
    setAddedSlugs((prev) => new Set(prev).add(variantSlug));
  };

  const goToCart = () => {
    onClose();
    setTimeout(() => setCartOpen(true), 150);
  };

  // Sort packs: pack-premiere-vidange first, then standard, then premium
  const PACK_ORDER = ["pack-premiere-vidange", "pack-entretien-standard", "pack-entretien-premium"];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="acc-suggestion-modal" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="text-xs font-mono-tech tracking-[0.2em] text-[#D8B86A] uppercase mb-2">— Mini-pelle ajoutée au panier</div>
          <DialogTitle className="font-heading text-2xl font-black text-[#072135] leading-tight">
            Pensez aux accessoires compatibles
          </DialogTitle>
          <DialogDescription className="text-[#4B5563] leading-relaxed">
            Voici les accessoires qui équipent parfaitement votre <span className="font-bold text-[#072135]">{machineName}</span>. Choisissez la taille puis ajoutez-les à votre devis.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-[#4B5563]">Chargement…</div>
        ) : accs.length === 0 ? (
          <div className="py-8 text-center text-[#4B5563]">
            <Wrench size={36} className="mx-auto mb-2 text-gray-300" />
            <p>Aucun accessoire référencé pour ce modèle. <Link to="/contact" className="text-[#D8B86A] hover:underline">Nous contacter</Link></p>
          </div>
        ) : (
          <div className="space-y-2.5 mt-3" data-testid="acc-suggestions">
            {(() => {
              const packs = supportsMaintenancePacks
                ? accs.filter((a) => a.categorie === "pack-entretien")
                       .sort((a, b) => PACK_ORDER.indexOf(a.slug) - PACK_ORDER.indexOf(b.slug))
                : [];
              const others = accs.filter((a) => a.categorie !== "pack-entretien");
              return (
                <>
                  {packs.length > 0 && (
                    <>
                      <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-[#D8B86A] font-black mt-1 mb-1">★ Recommandés avec votre mini-pelle</div>
                      {packs.map((acc) => <SuggestRow key={acc.slug} acc={acc} onAdd={handleAdd} addedSlugs={addedSlugs} featured />)}
                    </>
                  )}
                  {others.length > 0 && packs.length > 0 && (
                    <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-[#4B5563] mt-4 mb-1">Autres accessoires compatibles</div>
                  )}
                  {others.map((acc) => <SuggestRow key={acc.slug} acc={acc} onAdd={handleAdd} addedSlugs={addedSlugs} />)}
                </>
              );
            })()}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-2 pt-4 mt-2 border-t border-gray-200">
          <button onClick={onClose} data-testid="acc-suggest-close" className="flex-1 px-5 py-3 border-2 border-gray-300 hover:border-[#072135] text-[#072135] font-heading font-black uppercase tracking-wider text-xs rounded-sm transition-colors inline-flex items-center justify-center gap-2">
            <X size={14} /> Non merci, continuer
          </button>
          <button onClick={goToCart} data-testid="acc-suggest-go-cart" className="flex-1 px-5 py-3 bg-[#D8B86A] hover:bg-[#c2a45d] text-[#072135] font-heading font-black uppercase tracking-wider text-xs rounded-sm transition-colors inline-flex items-center justify-center gap-2">
            <ShoppingBag size={14} /> Voir mon panier
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
